<template>
  <div class="w-full mt-4 flex flex-col gap-4">
    <template v-if="isLoading">
      <GroupTableSkeleton />
    </template>

    <template v-else>
      <div class="overflow-x-auto rounded-lg shadow-sm border border-gray-200 min-h-[300px]">
        <div
          class="hidden md:grid grid-cols-12 gap-1 w-full items-center px-4 py-2 text-sm text-gray-500 font-semibold"
          style="background: var(--Neutral-LightGray-5, #fafafa)"
        >
          <div class="col-span-3">
            <p class="text-start">Nome</p>
          </div>
          <div class="col-span-3">
            <p class="text-start">Email</p>
          </div>
          <div class="col-span-2">
            <p class="text-start">Empresa</p>
          </div>
          <div class="col-span-3">
            <p class="text-start">Usuários</p>
          </div>
          <div class="col-span-1">
            <p class="text-end">Ações</p>
          </div>
        </div>

        <template v-if="filteredGroups.length > 0">
          <div
            v-for="item in paginatedGroups"
            :key="item.id"
            class="grid grid-cols-12 gap-1 w-full items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50 text-gray-600"
          >
            <div class="hidden md:block md:col-span-3">
              <p class="text-start">{{ item.name }}</p>
            </div>

            <div class="hidden md:block md:col-span-3 break-words">
              <p class="text-sm text-start">{{ item.email }}</p>
            </div>

            <div class="hidden md:block md:col-span-2">
              <p class="text-start">{{ item.company.name }}</p>
            </div>

            <div class="hidden md:block md:col-span-3">
              <TableChips
                :items="item.userGroups.map(({ user }) => ({ id: user.id, name: user.name }))"
              />
            </div>

            <div class="hidden md:flex md:col-span-1 gap-2 items-center justify-end">
              <v-icon
                data-testid="edit-group-button"
                @click="openModalUpdate(item)"
                class="hover:cursor-pointer"
                color="primary"
                size="small"
                :icon="mdiPencil"
              />
              <v-icon
                data-testid="delete-group-button"
                @click="openModalDelete(item.id, item.name)"
                class="hover:cursor-pointer"
                color="primary"
                size="small"
                :icon="mdiTrashCan"
              />
              <v-tooltip text="Visualizar permissões">
                <template #activator="{ props: tooltipProps }">
                  <v-icon
                    v-bind="tooltipProps"
                    data-testid="permissions-group-button"
                    @click="openPermissionsModal(item)"
                    class="hover:cursor-pointer"
                    color="primary"
                    size="small"
                    :icon="mdiShieldAccount"
                  />
                </template>
              </v-tooltip>
            </div>

            <!-- Mobile layout -->
            <div class="flex flex-col gap-2 md:hidden col-span-12">
              <p class="font-medium">{{ item.name }}</p>
              <p class="text-sm text-gray-500 break-words">{{ item.email }}</p>
              <p class="text-sm">{{ item.company.name }}</p>
              <TableChips
                :items="item.userGroups.map(({ user }) => ({ id: user.id, name: user.name }))"
              />
              <div class="flex gap-2 items-center pt-1">
                <v-icon
                  @click="openModalUpdate(item)"
                  class="hover:cursor-pointer"
                  color="primary"
                  size="small"
                  :icon="mdiPencil"
                />
                <v-icon
                  @click="openModalDelete(item.id, item.name)"
                  class="hover:cursor-pointer"
                  color="primary"
                  size="small"
                  :icon="mdiTrashCan"
                />
                <v-icon
                  data-testid="permissions-group-button-mobile"
                  @click="openPermissionsModal(item)"
                  class="hover:cursor-pointer"
                  color="primary"
                  size="small"
                  :icon="mdiShieldAccount"
                />
              </div>
            </div>
          </div>
        </template>

        <EmptyList v-else title="Nenhum grupo encontrado" class="py-12" />
      </div>

      <div v-if="filteredGroups.length > 0" class="flex justify-center py-2">
        <v-pagination
          v-model="currentPage"
          :total-visible="3"
          :length="pageCount"
          density="comfortable"
          color="primary"
          @update:model-value="changePage"
        />
      </div>
    </template>

    <teleport to="#app">
      <ConfirmDeleteModal
        @closeModal="showDeleteModal = false"
        :show="showDeleteModal"
        :isLoading="isLoadingDelete"
        @handleConfirm="handleDelete"
        :description="`Você tem certeza que deseja excluir o grupo ${groupToDelete.name}?`"
      />
      <UpdateGroupModal :group="groupToUpdate" />
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, computed, watch } from 'vue'
import { mdiPencil, mdiTrashCan, mdiShieldAccount } from '@mdi/js'
import { useGroupStore } from '@/pinia-store/useGroupStore'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useToast } from 'vue-toastification'
import ConfirmDeleteModal from '@/components/designSystem/modals/confirm.delete.action.vue'
import TableChips from '@/components/designSystem/Chips/TableChips.vue'
import GroupTableSkeleton from '@/components/designSystem/Skeleton/GroupTableSkeleton.vue'
import EmptyList from '@/components/EmptyList.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import { GroupResponse } from '@/Network/Types/Responses/GroupResponses'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import UpdateGroupModal from './modal/update.group.modal.vue'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'

const groupStore = useGroupStore()
const { filteredGroups, isLoading } = storeToRefs(groupStore)
const isLoadingDelete = ref(false)
const showDeleteModal = ref(false)
const groupToDelete = ref<{ name: string; id: number }>({ id: 0, name: '' })
const groupToUpdate = ref<GroupResponse | undefined>(undefined)
const toast = useToast()
const { notifyError } = useErrorToast()

const currentPage = ref(1)
const itemsPerPage = ref(8)

const paginatedGroups = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return filteredGroups.value.slice(start, start + itemsPerPage.value)
})

const pageCount = computed(() => Math.ceil(filteredGroups.value.length / itemsPerPage.value))

const changePage = (page: number) => {
  currentPage.value = page
}

watch(
  () => filteredGroups.value.length,
  () => {
    currentPage.value = 1
  }
)

onMounted(async () => {
  try {
    await groupStore.getAllGroups()
  } catch (error) {
    notifyError(error, 'Não foi possível carregar os grupos.')
  }
})

const openModalDelete = (id: number, name: string) => {
  showDeleteModal.value = true
  groupToDelete.value.id = id
  groupToDelete.value.name = name
}

const handleDelete = async () => {
  try {
    isLoadingDelete.value = true
    await groupStore.remove(groupToDelete.value.id)
    toast.success('Grupo removido com sucesso!')
    await groupStore.getAllGroups()
  } catch (error) {
    notifyError(error, 'Não foi possível excluir o grupo.')
  } finally {
    showDeleteModal.value = false
    isLoadingDelete.value = false
    groupToDelete.value = {
      id: 0,
      name: ''
    }
  }
}

const modalStore = useModalStore()
const openModalUpdate = (group: GroupResponse) => {
  groupToUpdate.value = group
  modalStore.toggleModal(EModals.UPDATE_GROUP_MODAL)
}

const openPermissionsModal = (group: GroupResponse) => {
  modalStore.toggleModal(EModals.PERMISSIONS_TREE, {
    subjectId: group.id,
    subjectType: AuthorizationResourcesAndSubjects.Group,
    subjectName: group.name
  })
}
</script>
